import Button from 'components/Button';
import DataTable from 'components/DataTable';
import Input from 'components/Input';
import ListActionsWrapper from 'components/ListActionsWrapper';
import ListTabs from 'components/ListTabs';
import ListWrapper from 'components/ListWrapper';
import Modal from 'components/Modal';
import Pagination from 'components/Pagination';
import ProductImage from 'components/ProductImage';
import SearchInput from 'components/SearchInput';
import Typography from 'components/Typography';
import {ProductTypeFilter} from 'hooks/useProductList';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DataItem, DataTableSchemaItem, Product} from 'types';
import styles from './Product.module.scss';
import api from 'api';
import toast from 'react-hot-toast';
import Select from 'components/Select';
import {useLocation} from 'react-router-dom';
import {get} from 'lodash';
import FormActionGroup from 'components/FormActionGroup';
import {Controller, useForm} from 'react-hook-form';
import DeleteModal from 'components/DeleteModal';
import {formatPrice} from 'utils/format-price';
import {useMutation} from 'react-query';
import useCompanyConfig from 'hooks/useCompanyConfig';

const SCHEMA: DataTableSchemaItem[] = [
  {dataKey: 'image', header: 'Image', colWidth: '100px'},
  {dataKey: 'name', header: 'Name', colWidth: '2fr', sortable: true},
  {dataKey: 'offering_type', header: 'Offering Type'},
  {dataKey: 'description', header: 'Description'},
  {dataKey: 'price', header: 'Price', sortable: true},
];

type Props = {
  products: Product[];
  totalPages: number;
  currentPage?: number;
  search?: string;
  refetch: () => void;
  onProductTypeChange: (value: ProductTypeFilter) => void;
  productType?: ProductTypeFilter;
  onSearchChange: (searchValue: string) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (value: string, sort: 'asc' | 'desc') => void;
  count?: number;
};

let modalVisible = false;

const deleteOfferingItem = async (id?: number) => {
  if (!id) return;
  return await api.delete(`/products/${id}`);
};

const Products: React.VFC<Props> = ({
  products,
  productType = '',
  onProductTypeChange,
  totalPages,
  search = '',
  onSearchChange,
  currentPage = 1,
  refetch,
  sortBy,
  sortOrder,
  onSort,
  count,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [visible, setVisible] = useState(modalVisible);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const modalRef = useRef(get(useLocation().state, 'isAdd', false));
  const {isDeliveryManager, isPickupDropoff, hasFeature} = useCompanyConfig();
  const {mutateAsync: deleteOffering, isLoading: isDeleting} = useMutation({
    mutationFn: deleteOfferingItem,
    onSuccess: () => {
      refetch();
      setSelectedProduct(undefined);
      setDeleteVisible(false);
      setVisible(false);
    },
    onError: (error) => {
      const message = get(
        error,
        'response.data.message',
        'Something went wrong'
      );
      toast.error(message);
    },
  });

  useEffect(() => {
    modalVisible = modalRef.current;
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange]
  );

  const handleSelectProduct = useCallback(
    (dataItem: DataItem) => {
      const product = products.find((item) => item.id === dataItem.id);
      setSelectedProduct(product);
      setVisible(true);
    },
    [products]
  );

  const tabs = useMemo(
    () =>
      hasFeature('hasServicesOnOffering')
        ? [
            {title: 'All', value: ''},
            {title: 'Services', value: 'service'},
            {title: 'Products', value: 'product'},
          ]
        : [
            {title: 'All', value: ''},
            {title: 'Products', value: 'product'},
          ],
    [hasFeature]
  );

  const data = useMemo(() => {
    return products.map((product) => ({
      ...product,
      id: product.id,
      image: <ProductImage product={product} />,
      name: product.name,
      offering_type: (
        <p className={styles.offeringType}>
          {product?.offering_type?.replaceAll('/', ' / ')}
        </p>
      ),
      description: product?.description,
      price: formatPrice(get(product, 'price', 0)),
    }));
  }, [products]);

  const onChangeTabs = useCallback(
    (nextValue: string) => {
      onProductTypeChange(nextValue as ProductTypeFilter);
    },
    [onProductTypeChange]
  );

  const deleteItem = () => {
    if (!selectedProduct?.id) return;
    deleteOffering(selectedProduct?.id);
  };

  const addNewProduct = () => {
    setSelectedProduct(undefined);
    setVisible(true);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: 20,
        }}
      >
        <Typography variant="h2">All Offerings</Typography>

        <Typography variant="h4">
          Total{' '}
          {productType === 'product'
            ? 'Products'
            : productType === 'service'
            ? 'Services'
            : 'Offerings'}
          : {count}
        </Typography>
      </div>
      <ListWrapper>
        <ListTabs
          tabs={tabs}
          isProductsPage={true}
          activeTabValue={productType}
          onChange={onChangeTabs}
        />
        <ListActionsWrapper>
          <SearchInput
            placeholder="Search"
            onChange={handleSearchChange}
            value={search}
          />
          <Button className={styles.button} onClick={addNewProduct}>
            {productType === 'service'
              ? 'Add Service'
              : productType === 'product'
              ? 'Add Product'
              : 'Add Offering'}
          </Button>
        </ListActionsWrapper>
        <DataTable
          data={data}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          schema={SCHEMA}
          selectable={false}
          onClick={handleSelectProduct}
        />
        <Pagination
          basePath="/products"
          page={currentPage}
          total={totalPages}
          setCurrentPage={() => {}}
        />
      </ListWrapper>
      {deleteVisible && (
        <DeleteModal
          openModal={deleteVisible}
          setOpenModal={setDeleteVisible}
          onDelete={deleteItem}
          text="Are you sure you want to delete this offering?"
          loading={isDeleting}
        />
      )}
      {visible && (
        <NewProduct
          productType={productType || 'product'}
          isTypeDisabled={isDeliveryManager || isPickupDropoff}
          onDelete={() => setDeleteVisible(true)}
          visible={visible || !!selectedProduct}
          product={selectedProduct}
          onClose={() => {
            setSelectedProduct(undefined);
            setVisible(false);
          }}
          setSelectedProduct={setSelectedProduct}
          onSuccess={refetch}
        />
      )}
    </>
  );
};

type ProductProps = {
  visible: boolean;
  onClose: () => void;
  product?: Product;
  onSuccess: (data: Product) => void;
  onDelete?: () => void;
  setSelectedProduct?: (data: Product) => void;
  productType?: ProductTypeFilter;
  isTypeDisabled?: boolean;
};

type FormValues = {
  offering_type: {value: string; label: string} | undefined;
  name: string;
  price: string;
  description: string;
  image: File | null;
};

export const NewProduct = ({
  productType,
  visible,
  onClose,
  onSuccess,
  product,
  onDelete,
  setSelectedProduct,
  isTypeDisabled,
}: ProductProps) => {
  const {control, handleSubmit, reset, setValue, watch} = useForm<FormValues>();
  const [description, setDescription] = useState(product?.description || '');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<any>(null);
  useEffect(() => {
    if (product) {
      setValue('name', product.name);
      setValue('price', product.price);
      setDescription(product.description);
      setValue('offering_type', {
        value: product?.offering_type,
        label:
          product?.offering_type[0].toUpperCase() +
          product?.offering_type.slice(1),
      });
    } else {
      setImage(null);
      setDescription('');
      reset();
      setValue(
        'offering_type',
        productType === 'service'
          ? {value: 'service', label: 'Service'}
          : productType === 'product'
          ? {value: 'product', label: 'Product'}
          : undefined
      );
    }
  }, [product, reset, setValue, productType]);

  const onSubmit = (data: any) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('offering_type', data.offering_type.value);
    formData.append('price', data.price.replace(/,/g, ''));
    formData.append('description', description);

    if (product?.image === undefined) {
      //@ts-ignore
      formData.append('image.file', null);
    }

    if (image) {
      formData.append('image.file', image);
    }

    const request = product
      ? api.put(`/products/${product.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      : api.post('products/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

    request
      .then((res) => {
        onSuccess(res.data);
        reset();
        setDescription('');
        setImage(null);
        onClose();
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    onClose();
    reset({
      name: '',
      offering_type: undefined,
      price: '',
      description: '',
    });
    setDescription('');
    setImage(null);
  };

  return (
    <Modal
      isOpen={visible}
      onClose={handleClose}
      className={styles.list_wrapper}
      onRequestClose={handleClose}
    >
      <Typography variant="h2">
        {product
          ? product?.name
          : watch('offering_type.value') === 'service'
          ? 'Add service'
          : watch('offering_type.value') === 'product'
          ? 'Add product'
          : 'Add offering'}
      </Typography>
      <div className={styles.label}>Image:</div>
      <ProductImage
        showDelete={true}
        size="medium"
        product={product}
        onChange={(value) => {
          if (setSelectedProduct && product?.id) {
            //@ts-ignore
            setSelectedProduct({...product, image: value});
          }
          setImage(value);
        }}
      />

      <div className={styles.label}>Type</div>
      <Controller
        name="offering_type"
        control={control}
        rules={{
          required: {
            value: true,
            message: 'Required',
          },
        }}
        render={({field: {ref, ...field}, fieldState}) => {
          return (
            <Select
              {...field}
              isDisabled={isTypeDisabled}
              error={fieldState.error?.message}
              options={[
                {
                  value: 'service',
                  label: 'Service',
                },
                {
                  value: 'product',
                  label: 'Product',
                },
              ]}
            />
          );
        }}
      />

      <div className={styles.label}>
        {watch('offering_type')?.value === 'service'
          ? 'Service '
          : watch('offering_type')?.value === 'product'
          ? 'Product '
          : ''}
        Name:
      </div>
      <Controller
        name="name"
        control={control}
        rules={{required: true}}
        render={({field, fieldState}) => (
          <Input
            {...field}
            placeholder={`Name`}
            hasError={!!fieldState.error}
            className={styles.root}
          />
        )}
      />

      <div className={styles.label}>Description:</div>
      <textarea
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        placeholder="Description"
        className={styles.textarea}
      />
      <div className={styles.label}>Price:</div>
      <Controller
        name="price"
        control={control}
        rules={{
          required: {
            value: true,
            message: 'Required',
          },
        }}
        render={({field, fieldState}) => (
          <Input
            {...field}
            hasError={!!fieldState.error?.message}
            onChange={(value) => {
              let val = value.target.value.replace(/,/g, '');
              let [integer, decimal] = val.split('.');
              integer = integer.replace(/\D/g, '');
              let formatted = Number(integer).toLocaleString();
              if (decimal !== undefined) {
                formatted += '.' + decimal.replace(/\D/g, '');
              }
              field.onChange(formatted);
            }}
            placeholder="Price"
            className={styles.root}
          />
        )}
      />

      <FormActionGroup className={styles.buttonWrap}>
        {product ? (
          <Button color="danger" size="md" onClick={onDelete}>
            Delete
          </Button>
        ) : (
          <></>
        )}

        <Button loading={loading} onClick={handleSubmit(onSubmit)}>
          {product ? 'Update' : 'Save'}
        </Button>
      </FormActionGroup>
    </Modal>
  );
};

export default Products;
